import {
  DB_SERIES_COLORS,
  DB_SERIES_FALLBACK_COLOR,
  MetricsInterval,
} from 'src/containers/metrics/constants';
import {
  DatabaseTimeseries,
  DbInstanceTimeseries,
  DbSeriesUnit,
  DbTimeseriesResponse,
  MetricsChartData,
} from 'src/containers/metrics/types';

import { serializeQueryParameters } from './helpers';
import { monitoringAPI } from './monitoring';
import { getScoringTimeseriesRange } from './scoringHealth';

const UNIT_ORDER: DbSeriesUnit[] = ['count', 'per_sec', 'ms', 'percent', 'mb'];

const UNIT_LABEL: Record<DbSeriesUnit, string> = {
  count: 'Counts',
  per_sec: 'Per second',
  ms: 'Latency',
  percent: 'Percent',
  mb: 'Megabytes',
};

const toChartTimestamp = (value: number) =>
  value < 1_000_000_000_000 ? value * 1000 : value;

const instanceLabel = (
  database: DatabaseTimeseries,
  instance: DbInstanceTimeseries
) => {
  const sameRole = database.instances.filter(
    (item) => item.role === instance.role
  ).length;

  if (instance.role === 'instance') {
    return sameRole > 1 ? instance.instanceId : null;
  }

  return sameRole > 1
    ? `${instance.role} ${instance.instanceId}`
    : instance.role;
};

export const fetchDbTimeseriesByDatabase = async (
  interval: MetricsInterval
): Promise<DbTimeseriesResponse> => {
  const { data } = await monitoringAPI.get<DbTimeseriesResponse>(
    `/dbhealth/timeseries/by-database${serializeQueryParameters(
      getScoringTimeseriesRange(interval)
    )}`
  );

  return data;
};

export const dbTimeseriesToCharts = (
  response: DbTimeseriesResponse,
  interval: MetricsInterval
): MetricsChartData[] => {
  const timestamps = response.timestamps.map(toChartTimestamp);
  const stepMs = response.stepSeconds * 1000;

  return response.databases.flatMap((database) => {
    const displayName = database.label || database.name;

    return database.instances.flatMap((instance) => {
      const byUnit = new Map<string, typeof instance.series>();

      instance.series.forEach((series) => {
        const current = byUnit.get(series.unit) || [];
        current.push(series);
        byUnit.set(series.unit, current);
      });

      const units = [
        ...UNIT_ORDER.filter((unit) => byUnit.has(unit)),
        ...Array.from(byUnit.keys()).filter(
          (unit) => !UNIT_ORDER.includes(unit as DbSeriesUnit)
        ),
      ];
      const role = instanceLabel(database, instance);

      return units.map((unit) => ({
        id: `${database.targetId}:${instance.instanceId}:${unit}`,
        title: [displayName, role, UNIT_LABEL[unit as DbSeriesUnit] || unit]
          .filter(Boolean)
          .join(' · ')
          .concat(` (${database.status})`),
        interval,
        timestamps,
        stepMs,
        series: (byUnit.get(unit) || []).map((item) => ({
          key: item.key,
          name: item.name,
          color: DB_SERIES_COLORS[item.key] || DB_SERIES_FALLBACK_COLOR,
          values: item.values,
          stats: {
            mean: item.stats?.mean ?? null,
            last: item.stats?.last ?? null,
            max: item.stats?.max ?? null,
            total: item.stats?.total ?? null,
          },
        })),
      }));
    });
  });
};
